import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createGoafreetTheme } from '../theme/muiTheme';
import { designTokens } from '../theme/designTokens';
import { initNumberKeyboardEnforcer } from '../utils/numberKeyboardEnforcer';

const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
  isDark: false,
  tokens: designTokens,
});

const getInitialMode = () => {
  if (typeof window === 'undefined') return 'light';
  const savedTheme = localStorage.getItem('goafreet-theme');
  if (savedTheme === 'light' || savedTheme === 'dark') return savedTheme;
  
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  
  // Night time: 6:30 PM (18:30) to 6:00 AM (06:00)
  const isNightTime = hours > 18 || (hours === 18 && minutes >= 30) || hours < 6;
  return isNightTime ? 'dark' : 'light';
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    initNumberKeyboardEnforcer();
    // The initial mode is read once by getInitialMode; React state remains the live source of truth.
  }, []);

  useEffect(() => {
    if (mounted && typeof window !== 'undefined') {
      localStorage.setItem('goafreet-theme', mode);
      document.documentElement.setAttribute('data-theme', mode);
      document.documentElement.style.colorScheme = mode;
      document.body.setAttribute('data-theme', mode);
      document.body.classList.toggle('theme-dark', mode === 'dark');
    }
  }, [mode, mounted]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Check periodically if the theme should change automatically based on time
    // Only applies if the user hasn't explicitly set a theme preference
    const interval = setInterval(() => {
      const savedTheme = localStorage.getItem('goafreet-theme');
      if (!savedTheme) {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const isNightTime = hours > 18 || (hours === 18 && minutes >= 30) || hours < 6;
        setMode(isNightTime ? 'dark' : 'light');
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => createGoafreetTheme(mode), [mode]);
  const themeTokens = useMemo(() => designTokens, []);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
      isDark: mode === 'dark',
      tokens: themeTokens,
    }),
    [mode, themeTokens]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <StylesThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </StylesThemeProvider>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export { designTokens };
export default ThemeContext;
